/**
 * Utility functions for calculating table totals and related operations
 */

import { useMemo } from 'react';

/**
 * Calculate totals for columns with total property
 * @param {Array} rows - Table data rows
 * @param {Array} columns - Table column definitions
 * @returns {Object} - Object containing total values keyed by column key
 */
export const calculateColumnTotals = (rows, columns) => {
  const totalValues = {};
  columns.forEach((column) => {
    if (column.total) {
      const sum = rows.reduce((acc, row) => {
        const value = column.valueGetter
          ? column.valueGetter(row)
          : row[column.key];
        return acc + (Number(value) || 0);
      }, 0);
      totalValues[column.key] = sum.toFixed(2);
    }
  });
  return totalValues;
};

/**
 * Custom hook to calculate column totals and related span information
 * @param {Array} rows - Table data rows
 * @param {Array} columns - Table column definitions
 * @param {boolean} showCheckbox - Whether checkbox column is shown
 * @param {boolean} showSlNo - Whether Sl No column is shown
 * @param {Array} displayedRows - Only the rows currently displayed (for pagination)
 * @returns {Object} - Object containing totals and span information
 */
export const useTableTotals = (rows, columns, showCheckbox = true, showSlNo = true, displayedRows = null) => {
  // Calculate totals for columns with total property
  const totals = useMemo(() => {
    // Use displayedRows if provided, otherwise use all rows
    const rowsToCalculate = displayedRows || rows;
    return calculateColumnTotals(rowsToCalculate, columns);
  }, [rows, columns, displayedRows]);

  // Find first and last columns with totals for spanning
  const firstTotalIndex = useMemo(() => {
    return columns.findIndex((col) => col.total);
  }, [columns]);
  
  const lastTotalIndex = useMemo(() => {
    return columns
      .map((col, idx) => (col.total ? idx : -1))
      .filter((idx) => idx !== -1)
      .slice(-1)[0];
  }, [columns]);

  // Calculate colspan for total label - properly accounting for showCheckbox and showSlNo
  const totalLabelColspan = useMemo(() => {
    let colspan = 0;
    if (firstTotalIndex >= 0) {
      // Start with the index of the first total column
      colspan = firstTotalIndex;

      // Add 1 for checkbox column if it's shown
      if (showCheckbox) {
        colspan += 1;
      }

      // Add 1 for serial number column if it's shown
      if (showSlNo) {
        colspan += 1;
      }
    }
    return colspan;
  }, [firstTotalIndex, showCheckbox, showSlNo]);

  return {
    totals,
    firstTotalIndex,
    lastTotalIndex,
    totalLabelColspan
  };
};